import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Tharros COPD Study (internal reference)
 * 
 * Inclusion criteria:
 * - Age 40-80 (handled by platform)
 * - COPD diagnosis
 * - Current or former smoker
 * - CAT score >=10 (using exacerbations/impact as proxy)
 * - At least 1 CV risk factor
 * - No ICS use within 12 months
 */
export const tharrosCOPDScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "copd_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with COPD (Chronic Obstructive Pulmonary Disease) by a healthcare provider?",
    helpText: "COPD is also known as emphysema or chronic bronchitis",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      {
        value: "no",
        label: "No",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for people who have been diagnosed with COPD by a healthcare provider.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 26049, booleanAttributeValue: true }], // has_COPD_prerequisite
      no: [{ attributeDefinitionId: 26049, booleanAttributeValue: false }],
    }),
  },
  {
    id: "age",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "under_40",
        label: "Under 40",
        disqualify: true,
      },
      {
        value: "40_80",
        label: "40-80",
      },
      {
        value: "over_80",
        label: "Over 80",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only available to participants between 40-80 years of age.",
    attributeMapping: mappers.value({
      under_40: [],
      over_80: [],
      40_80: [],
    }),
  },
  {
    id: "smoking_history",
    type: QuestionType.RADIO,
    question: "Which of the following best describes your smoking history?",
    required: true,
    allowBack: true,
    options: [
      { value: "current_smoker", label: "I currently smoke cigarettes" },
      { value: "former_smoker", label: "I used to smoke cigarettes but have quit" },
      { 
        value: "never_smoked", 
        label: "I have never smoked cigarettes regularly",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants who are current or former cigarette smokers.",
    attributeMapping: mappers.value({
      current_smoker: [
        { attributeDefinitionId: 20917, booleanAttributeValue: true }, // is_smoker
      ],
      former_smoker: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true }, // is_pastsmoker
      ],
      never_smoked: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "copd_symptoms",
    type: QuestionType.RADIO,
    question: "Regarding your COPD symptoms, which option is most true for you?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "never_cough", 
        label: "I never cough",
        disqualify: true,
      },
      { value: "rarely_cough", label: "I rarely cough" },
      { value: "some_cough", label: "I cough some of the time" },
      { value: "lot_cough", label: "I cough a lot of the time" },
      { value: "most_cough", label: "I cough most of the time" },
      { value: "always_cough", label: "I cough all of the time" },
    ],
    disqualifyMessage:
      "This study requires participants to have COPD symptoms including coughing.",
    attributeMapping: mappers.value({
      never_cough: [],
      rarely_cough: [],
      some_cough: [],
      lot_cough: [],
      most_cough: [],
      always_cough: [],
    }),
  },
  {
    id: "phlegm_difficulty",
    type: QuestionType.RADIO,
    question: "Do you have phlegm or sputum in your chest that is difficult to cough up?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "no_phlegm", 
        label: "No, I do not have any phlegm in my chest at all",
        disqualify: true,
      },
      { value: "little_easy", label: "I have a little bit of phlegm in my chest, but it is NOT difficult to cough up" },
      { value: "some_easy", label: "I have some phlegm in my chest, but i can cough it up" },
      { value: "difficult_cough", label: "I have phlegm in my chest that is difficult to cough up" },
      { value: "lot_phlegm", label: "I have a lot of phlegm in my chest" },
      { value: "full_phlegm", label: "My chest is full of phlegm" },
    ],
    disqualifyMessage:
      "This study requires participants to have COPD symptoms including phlegm or mucus production.",
    attributeMapping: mappers.value({
      no_phlegm: [],
      little_easy: [],
      some_easy: [],
      difficult_cough: [],
      lot_phlegm: [],
      full_phlegm: [],
    }),
  },
  {
    id: "cv_risk_factors",
    type: QuestionType.RADIO,
    question: "Do you have any of the following health conditions or risk factors?",
    helpText: "You can only click on one option; if more than one is true simply select the first one that applies to you",
    required: true,
    allowBack: true,
    options: [
      { value: "heart_disease", label: "Heart disease (such as angina, heart attack, stents, or bypass surgery)" },
      { value: "artery_narrowing", label: "Narrowing or blockage in your heart or leg arteries" },
      { value: "stroke", label: "Stroke or mini-stroke (TIA)" },
      { value: "hypertension", label: "High blood pressure (hypertension)" },
      { value: "diabetes", label: "Diabetes (Type 1 or Type 2)" },
      { value: "high_cholesterol", label: "High cholesterol or unhealthy lipid levels" },
      { value: "kidney_disease", label: "Kidney disease (moderate or chronic — e.g., eGFR between 20–60)" },
      { value: "obesity", label: "Obesity (BMI of 30 or higher)" },
      { value: "high_risk_calculator", label: "I've been told I have a high risk of heart disease or stroke based on a risk calculator (like QRISK, ASCVD or Framingham)" },
      { value: "calcium_buildup", label: "A heart scan showed calcium buildup (also called coronary artery calcification)" },
      { 
        value: "none", 
        label: "I don't have any of these conditions",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants to have at least one cardiovascular risk factor.",
    attributeMapping: mappers.value({
      heart_disease: [
        { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease
      ],
      artery_narrowing: [
        { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease (as proxy)
      ],
      stroke: [
        { attributeDefinitionId: 1312, booleanAttributeValue: true }, // had_CVA
      ],
      hypertension: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
      ],
      diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES, booleanAttributeValue: true },
      ],
      high_cholesterol: [
        { attributeDefinitionId: 23369, booleanAttributeValue: true }, // has_hyperlipidemia
      ],
      kidney_disease: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
      ],
      obesity: [
        { attributeDefinitionId: 21504, booleanAttributeValue: true }, // has_overweight_obesity
      ],
      high_risk_calculator: [
        { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease (as proxy)
      ],
      calcium_buildup: [
        { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease (as proxy)
      ],
      none: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES, booleanAttributeValue: false },
        { attributeDefinitionId: 23369, booleanAttributeValue: false },
        { attributeDefinitionId: 3436, booleanAttributeValue: false },
        { attributeDefinitionId: 1312, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: 21504, booleanAttributeValue: false }, // has_overweight_obesity
      ],
    }),
  },
  {
    id: "inhaled_steroid_use",
    type: QuestionType.RADIO,
    question: "Are you currently using any inhaled corticosteroid medications for your COPD? These include preventer inhalers such as Flovent, Pulmicort, QVAR, Advair, Symbicort, Breo or Trelegy",
    helpText: "Inhaled corticosteroid are often included in daily COPD maintenance treatment",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "yes_currently", 
        label: "Yes, I currently use an inhaled corticosteroid",
        disqualify: true,
      },
      { 
        value: "yes_recently", 
        label: "Yes, I've used one in the past 12 months but not currently",
        disqualify: true,
      },
      { value: "no", label: "No, I haven't used any inhaled corticosteroids in the past 12 months" },
    ],
    disqualifyMessage:
      "This study cannot include people who have used inhaled corticosteroid medications within the past 12 months.",
    attributeMapping: mappers.value({
      yes_currently: [
        { attributeDefinitionId: 23810, booleanAttributeValue: true }, // has_taken_ICS_12_months
      ],
      yes_recently: [
        { attributeDefinitionId: 23810, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 23810, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question: "Are you currently enrollling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I am not enrolling in or participating in any other clinical trials" },
      { value: "yes", label: "Yes, I am currently enrolling in or participating in another clinical trial", disqualify: true },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
  {
    id: "willing_to_attend",
    type: QuestionType.RADIO,
    question: "Are you willing to attend study visits at a research site and be involved in this study for up to 3 years?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I am willing to attend regular in-person or telehealth visits" },
      {
        value: "no",
        label: "No, I cannot commit to regular visits",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss this schedule further with the research team",
      },
    ],
    disqualifyMessage:
      "This study requires attendance at in-person visits.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
      no: [{ attributeDefinitionId: 23479, booleanAttributeValue: false }],
      discuss: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
    }),
  },
];